import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import UIkit from "uikit";
import feather from "feather-icons";

import CRMService from "../../../services/crm.service";
import CRMPipelineService from "../../../services/crmPipeline.service";
import Dialog from "../../common/Dialog";
import SearchableDropdown from "../../common/SearchableDropdown";
import { AppContexts } from "../../../providers";
import { getMissingGuestLeadFields, opportunitySaveError } from "../opportunity-utils";

const EMPTY_FORM = {
  title: "",
  guest_first_name: "",
  guest_last_name: "",
  guest_phone: "",
  guest_email: "",
  owner_first_name: "",
  owner_last_name: "",
  owner_phone: "",
  owner_email: "",
  owner_address: "",
  owner_contact_id: null,
  check_in: "",
  check_out: "",
  guests_count: "",
  properties: [],
  property_details: "",
  description: "",
  source: "",
  value_amount: "",
  expected_close_date: "",
  next_follow_up_at: "",
};

const FeatherIcon = ({ name, size = 16 }) => {
  const icon = feather.icons[name];
  if (!icon) return null;
  return (
    <svg
      className="feather-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: icon.contents }}
    />
  );
};

const FormCustomSelect = ({ value, onChange, options }) => {
  const selectedOption = options.find((option) => `${option.value}` === `${value}`) || options[0];

  return (
    <div className="uk-inline crm-form-custom-select">
      <div uk-form-custom="target: > * > span:first-child">
        <select value={value} onChange={onChange}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button className="uk-button uk-button-default" type="button" tabIndex="-1">
          <span>{selectedOption?.label || ""}</span>
          <span uk-icon="icon: chevron-down"></span>
        </button>
      </div>
    </div>
  );
};

const formatMoney = (amount, currency = "USD") => {
  if (amount == null || amount === "") return null;
  const value = Number(amount);
  if (Number.isNaN(value)) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

const normalizeDate = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const formatDisplayDate = (value) => {
  const normalized = normalizeDate(value);
  if (!normalized) return "";
  const [year, month, day] = normalized.split("-").map(Number);
  if (!year || !month || !day) return normalized;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
};

const getMetadata = (opportunity) => {
  if (!opportunity?.metadata_json) return {};
  if (typeof opportunity.metadata_json === "string") {
    try {
      return JSON.parse(opportunity.metadata_json);
    } catch (err) {
      return {};
    }
  }
  return opportunity.metadata_json;
};

const guestLeadTitle = (form) => {
  const name = [form.guest_first_name, form.guest_last_name].filter(Boolean).join(" ").trim();
  return name || form.title || "Guest Lead";
};

const ownerLeadTitle = (form) => {
  const name = [form.owner_first_name, form.owner_last_name].filter(Boolean).join(" ").trim();
  return name || form.title || "Owner Opportunity";
};

const guestLabel = (guest) => {
  if (!guest) return "";
  const name = [guest.first_name, guest.last_name].filter(Boolean).join(" ").trim();
  return name || guest.display_name || guest.email || guest.formatted_phone || guest.phone || "Guest";
};

const guestPhone = (guest) => guest?.formatted_phone || guest?.phone_e164 || guest?.phone || "";

const guestId = (guest) => guest?.guest_id || null;

const customFieldEntityId = (entity) => entity?.crm_identity_id || entity?.id || entity?.guest_id || null;

const propertyLabel = (property) => {
  if (!property) return "";
  return [property.pms_id, property.address].filter(Boolean).join(" - ");
};

const pipelinePropertyLabel = (lead, properties) => {
  const selected = lead.properties?.[0] || lead.rentals?.[0] || null;
  const pmsId = selected?.pms_id || lead.property_details || "";
  const rental = properties.find((property) => `${property.pms_id}` === `${pmsId}`);
  const selectedLabel = selected?.pms_id && selected?.address ? propertyLabel(selected) : "";
  return selectedLabel || propertyLabel(rental) || propertyLabel(selected) || lead.property_details || "";
};

const pipelineContactPath = (type, opportunity, lead) => {
  if (type === "guest") {
    const id = opportunity.primary_crm_identity_id || lead.crm_identity_id || opportunity.primary_guest_id || lead.guest_id;
    return id ? `/crm/guests/${id}` : null;
  }

  const id = lead.crm_identity_id || opportunity.primary_crm_identity_id || opportunity.primary_contact_id || lead.contact_id;
  return id ? `/crm/owners/${id}` : null;
};

const customFieldValueFromRow = (row, definition) => {
  if (!row) return "";
  if (definition.field_type === "number") return row.value_number ?? "";
  if (definition.field_type === "date") return normalizeDate(row.value_date);
  if (definition.field_type === "boolean") return row.value_boolean ?? false;
  return row.value_text ?? "";
};

const CustomFieldFormInputs = ({ definitions, values, loading, onChange }) => {
  if (loading) return null;
  if (!definitions.length) return null;

  return (
    <div className="uk-margin-top">
      <div className="crm-section-title">Custom Fields</div>
      <div className="cf-values-grid">
        {definitions.map((definition) => {
          const value = values[definition.id];
          const fieldValue = value ?? (definition.field_type === "boolean" ? false : "");

          if (definition.field_type === "textarea") {
            return (
              <div className="cf-value-row" key={definition.id}>
                <label className="uk-form-label">{definition.field_label}</label>
                <div className="cf-value-input">
                  <textarea
                    className="uk-textarea cf-textarea"
                    rows={2}
                    value={fieldValue || ""}
                    onChange={(e) => onChange(definition.id, e.target.value)}
                  />
                </div>
              </div>
            );
          }

          if (definition.field_type === "select") {
            const options = Array.isArray(definition.options_json) ? definition.options_json : [];
            return (
              <div className="cf-value-row" key={definition.id}>
                <label className="uk-form-label">{definition.field_label}</label>
                <div className="cf-value-input">
                  <select
                    className="uk-select cf-select"
                    value={fieldValue || ""}
                    onChange={(e) => onChange(definition.id, e.target.value)}
                  >
                    <option value="">—</option>
                    {options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            );
          }

          if (definition.field_type === "boolean") {
            return (
              <div className="cf-value-row" key={definition.id}>
                <label className="uk-form-label">{definition.field_label}</label>
                <div className="cf-value-input">
                  <label className="cf-toggle">
                    <input
                      type="checkbox"
                      checked={!!fieldValue}
                      onChange={(e) => onChange(definition.id, e.target.checked)}
                    />
                    <span className="cf-toggle-slider"></span>
                  </label>
                </div>
              </div>
            );
          }

          return (
            <div className="cf-value-row" key={definition.id}>
              <label className="uk-form-label">{definition.field_label}</label>
              <div className="cf-value-input">
                <input
                  className="uk-input cf-input"
                  type={definition.field_type === "number" ? "number" : definition.field_type === "date" ? "date" : "text"}
                  value={fieldValue ?? ""}
                  onChange={(e) => onChange(definition.id, e.target.value)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const guestCountOptions = [
  { value: "", label: "Select Guests" },
  ...Array.from({ length: 20 }, (_, index) => ({
    value: `${index + 1}`,
    label: `${index + 1}`,
  })),
];

const offcanvasId = "#crm-opportunity-offcanvas";

const CRMPipelines = () => {
  const history = useHistory();
  const { pipelineType } = useParams();
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState(null);
  const [board, setBoard] = useState({ pipeline: null, stages: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [guestMode, setGuestMode] = useState("new");
  const [guestSearchTerm, setGuestSearchTerm] = useState("");
  const [guestSearchResults, setGuestSearchResults] = useState([]);
  const [guestSearchLoading, setGuestSearchLoading] = useState(false);
  const [guestSearchComplete, setGuestSearchComplete] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
  const rentalContext = useContext(AppContexts.RentalsContext);
  const authContext = useContext(AppContexts.AuthContext);

  const selectedPipeline = useMemo(
    () => pipelines.find((pipeline) => `${pipeline.id}` === `${selectedPipelineId}`) || null,
    [pipelines, selectedPipelineId]
  );

  const isGuestLead = selectedPipeline?.pipeline_type === "guest_leads";
  const isOwnerAcquisition = selectedPipeline?.pipeline_type === "owner_acquisition";
  const customFieldsEntityType = isGuestLead ? "guest" : isOwnerAcquisition ? "contact" : null;

  const stageOptions = useMemo(
    () => (board.stages || []).map((stage) => ({ value: stage.id, label: stage.name })),
    [board.stages]
  );

  const propertyValues = useMemo(
    () =>
      (rentalContext.rentals || [])
        .filter((rental) => rental.pms_id)
        .sort((first, second) => String(first.pms_id).localeCompare(String(second.pms_id))),
    [rentalContext.rentals]
  );

  const propertyOptions = useMemo(
    () => [
      { value: "", label: "Select Property" },
      ...propertyValues.map((property) => ({
        value: property.pms_id,
        label: propertyLabel(property),
      })),
    ],
    [propertyValues]
  );

  const selectedPropertyValue = form.properties?.[0]?.pms_id || form.property_details || "";

  const handlePropertyChange = (pmsId) => {
    const property = propertyValues.find((item) => item.pms_id === pmsId);
    setForm({
      ...form,
      properties: property ? [property] : [],
      property_details: pmsId || "",
    });
  };

  useEffect(() => {
    if (!rentalContext.rentals?.length && !rentalContext.loading) {
      rentalContext.get();
    }
  }, []);

  const loadPipelines = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CRMPipelineService.getPipelines();
      const list = res.data || [];
      setPipelines(list);

      const next =
        list.find((pipeline) => pipeline.pipeline_type === pipelineType) ||
        list[0] ||
        null;

      if (next) {
        setSelectedPipelineId(next.id);
        if (next.pipeline_type !== pipelineType) {
          history.replace(`/crm/pipelines/${next.pipeline_type}`);
        }
      }
    } catch (err) {
      UIkit.notification(err?.response?.data?.error || "Unable to load pipelines", "danger");
    } finally {
      setLoading(false);
    }
  }, [history, pipelineType]);

  const loadBoard = useCallback(async (pipelineId) => {
    if (!pipelineId) return;
    setLoading(true);
    try {
      const res = await CRMPipelineService.getBoard(pipelineId);
      setBoard(res.data || { pipeline: null, stages: [] });
    } catch (err) {
      UIkit.notification(err?.response?.data?.error || "Unable to load pipeline board", "danger");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPipelines();
  }, [loadPipelines]);

  useEffect(() => {
    loadBoard(selectedPipelineId);
  }, [loadBoard, selectedPipelineId]);

  useEffect(() => {
    if (!customFieldsEntityType) {
      setCustomFieldDefinitions([]);
      setCustomFieldValues({});
      return;
    }

    let active = true;
    setCustomFieldsLoading(true);
    CRMService.getCustomFieldDefinitions(customFieldsEntityType)
      .then((res) => {
        if (!active) return;
        setCustomFieldDefinitions(res?.data || []);
      })
      .catch((err) => {
        if (!active) return;
        setCustomFieldDefinitions([]);
        UIkit.notification(err?.response?.data?.error || "Unable to load custom fields", "danger");
      })
      .finally(() => {
        if (!active) return;
        setCustomFieldsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [customFieldsEntityType]);

  const activeCustomFieldsEntityId = useMemo(() => {
    if (!editing || !customFieldsEntityType) return null;
    if (isGuestLead) return customFieldEntityId(selectedGuest);
    if (isOwnerAcquisition) return form.owner_contact_id;
    return null;
  }, [customFieldsEntityType, editing, form.owner_contact_id, isGuestLead, isOwnerAcquisition, selectedGuest]);

  useEffect(() => {
    if (!customFieldsEntityType || !activeCustomFieldsEntityId || !customFieldDefinitions.length) {
      if (!activeCustomFieldsEntityId) setCustomFieldValues({});
      return;
    }

    let active = true;
    setCustomFieldsLoading(true);
    CRMService.getCustomFieldValues(customFieldsEntityType, activeCustomFieldsEntityId)
      .then((res) => {
        if (!active) return;
        const valueMap = {};
        (res?.data || []).forEach((row) => {
          const definition = customFieldDefinitions.find((def) => `${def.id}` === `${row.field_definition_id}`);
          if (definition) valueMap[definition.id] = customFieldValueFromRow(row, definition);
        });
        setCustomFieldValues(valueMap);
      })
      .catch((err) => {
        if (!active) return;
        setCustomFieldValues({});
        UIkit.notification(err?.response?.data?.error || "Unable to load custom field values", "danger");
      })
      .finally(() => {
        if (!active) return;
        setCustomFieldsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [activeCustomFieldsEntityId, customFieldDefinitions, customFieldsEntityType]);

  useEffect(() => {
    if (!isGuestLead || guestMode !== "existing") {
      setGuestSearchResults([]);
      setGuestSearchComplete(false);
      return undefined;
    }

    const searchTerm = guestSearchTerm.trim();
    if (selectedGuest && searchTerm === guestLabel(selectedGuest)) {
      setGuestSearchResults([]);
      setGuestSearchComplete(false);
      return undefined;
    }

    if (searchTerm.length < 2) {
      setGuestSearchResults([]);
      setGuestSearchComplete(false);
      return undefined;
    }

    let active = true;
    const timer = setTimeout(async () => {
      setGuestSearchLoading(true);
      setGuestSearchComplete(false);
      try {
        const res = await CRMService.searchGuests({
          search_term: searchTerm,
          sort: "updated_at",
          per: "8",
          page: 0,
        });
        if (!active) return;
        setGuestSearchResults(res.data?.paginated || res.data || []);
        setGuestSearchComplete(true);
      } catch (err) {
        if (!active) return;
        UIkit.notification(err?.response?.data?.error || "Unable to search guests", "danger");
      } finally {
        if (!active) return;
        setGuestSearchLoading(false);
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [guestMode, guestSearchTerm, isGuestLead, selectedGuest]);

  const selectGuest = (guest) => {
    setSelectedGuest(guest);
    setGuestSearchTerm(guestLabel(guest));
    setGuestSearchResults([]);
    setGuestSearchComplete(false);
    setForm((current) => ({
      ...current,
      guest_first_name: guest.first_name || "",
      guest_last_name: guest.last_name || "",
      guest_phone: guestPhone(guest),
      guest_email: guest.email || "",
    }));
  };

  const openForm = (opportunity = null, stage = null) => {
    const metadata = getMetadata(opportunity);
    const guestLead = metadata.guest_lead || {};
    const ownerLead = metadata.owner_acquisition || {};
    const linkedGuest = opportunity?.primary_guest_id || guestLead.guest_id || opportunity?.primary_crm_identity_id || guestLead.crm_identity_id
      ? {
          id: opportunity?.primary_crm_identity_id || guestLead.crm_identity_id || opportunity?.primary_guest_id || guestLead.guest_id,
          crm_identity_id: opportunity?.primary_crm_identity_id || guestLead.crm_identity_id || null,
          guest_id: opportunity?.primary_guest_id || guestLead.guest_id || null,
          first_name: guestLead.first_name || "",
          last_name: guestLead.last_name || "",
          email: guestLead.email || "",
          phone: guestLead.phone || "",
          formatted_phone: guestLead.phone || "",
          display_name: guestLead.display_name || "",
        }
      : null;

    setGuestMode(linkedGuest ? "existing" : "new");
    setSelectedGuest(linkedGuest);
    setGuestSearchTerm(linkedGuest ? guestLabel(linkedGuest) : "");
    setGuestSearchResults([]);
    setEditing(opportunity ? { ...opportunity, stage_id: opportunity.stage_id } : { stage_id: stage?.id });
    setCustomFieldValues({});
    setForm(
      opportunity
        ? {
            title: opportunity.title || "",
            guest_first_name: guestLead.first_name || "",
            guest_last_name: guestLead.last_name || "",
            guest_phone: guestLead.phone || "",
            guest_email: guestLead.email || "",
            owner_first_name: ownerLead.first_name || "",
            owner_last_name: ownerLead.last_name || "",
            owner_phone: ownerLead.phone || "",
            owner_email: ownerLead.email || "",
            owner_address: ownerLead.address || "",
            owner_contact_id: opportunity.primary_contact_id || ownerLead.contact_id || null,
            check_in: normalizeDate(guestLead.check_in),
            check_out: normalizeDate(guestLead.check_out),
            guests_count: guestLead.guests_count ?? "",
            properties: guestLead.properties || guestLead.rentals || [],
            property_details: guestLead.property_details || "",
            description: opportunity.description || "",
            source: opportunity.source || "",
            value_amount: opportunity.value_amount || "",
            expected_close_date: normalizeDate(opportunity.expected_close_date),
            next_follow_up_at: normalizeDate(opportunity.next_follow_up_at),
          }
        : EMPTY_FORM
    );
    UIkit.offcanvas(offcanvasId).show();
  };

  const closeForm = () => {
    UIkit.offcanvas(offcanvasId).hide();
    setEditing(null);
    setForm(EMPTY_FORM);
    setGuestMode("new");
    setSelectedGuest(null);
    setGuestSearchTerm("");
    setGuestSearchResults([]);
    setGuestSearchComplete(false);
    setCustomFieldValues({});
  };

  const handleCustomFieldChange = (definitionId, value) => {
    setCustomFieldValues((current) => ({ ...current, [definitionId]: value }));
  };

  const saveCustomFields = async (entityType, entityId) => {
    if (!entityType || !entityId || !customFieldDefinitions.length) return;

    const fields = customFieldDefinitions
      .filter((definition) => Object.prototype.hasOwnProperty.call(customFieldValues, definition.id))
      .map((definition) => ({
        field_definition_id: definition.id,
        value: customFieldValues[definition.id],
      }));

    if (!fields.length) return;

    await CRMService.bulkUpsertCustomFieldValues({
      entity_type: entityType,
      entity_id: entityId,
      fields,
    });
  };

  const handlePipelineChange = (pipeline) => {
    if (!pipeline) return;
    setSelectedPipelineId(pipeline.id);
    history.push(`/crm/pipelines/${pipeline.pipeline_type}`);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedPipeline) return;

    const stageId = editing?.stage_id || board.stages?.[0]?.id;
    if (!stageId) {
      UIkit.notification("Add a pipeline stage before saving an opportunity", "danger");
      return;
    }

    const selectedStage = (board.stages || []).find((stage) => `${stage.id}` === `${stageId}`);
    if (!selectedStage) {
      UIkit.notification("Choose a valid pipeline stage before saving", "danger");
      return;
    }

    setSaving(true);
    try {
      let opportunityGuest = selectedGuest;
      let ownerContact = null;

      if (isGuestLead) {
        if (guestMode === "existing" && !opportunityGuest) {
          UIkit.notification("Select an existing guest or switch to New Guest", "danger");
          setSaving(false);
          return;
        }

        const missingFields = getMissingGuestLeadFields(form, guestMode);

        if (missingFields.length) {
          UIkit.notification(`${missingFields.join(", ")} required`, "danger");
          setSaving(false);
          return;
        }

        if (guestMode === "new") {
          const guestRes = await CRMService.insertGuest({
            first_name: form.guest_first_name,
            last_name: form.guest_last_name,
            phone: form.guest_phone,
            email: form.guest_email,
          });
          opportunityGuest = guestRes.data;
        }

        await saveCustomFields("guest", customFieldEntityId(opportunityGuest));
      }

      if (isOwnerAcquisition) {
        const missingFields = [];
        if (!form.owner_first_name.trim()) missingFields.push("First Name");
        if (!form.owner_last_name.trim()) missingFields.push("Last Name");
        if (!form.owner_phone.trim()) missingFields.push("Mobile Phone");

        if (missingFields.length) {
          UIkit.notification(`${missingFields.join(", ")} required`, "danger");
          setSaving(false);
          return;
        }

        const ownerPayload = {
          ...(form.owner_contact_id ? { id: form.owner_contact_id } : {}),
          account_id: authContext.user?.account_id,
          first_name: form.owner_first_name,
          last_name: form.owner_last_name,
          phone: form.owner_phone,
          email: form.owner_email,
          category: "owner",
          tags: [],
          rentals: [],
        };

        const ownerRes = form.owner_contact_id
          ? await CRMService.updateContact(ownerPayload)
          : await CRMService.insertContact(ownerPayload);
        ownerContact = ownerRes.data;

        await saveCustomFields("contact", ownerContact?.crm_identity_id || ownerContact?.id || form.owner_contact_id);
      }

      const primaryGuestId = isGuestLead ? guestId(opportunityGuest) : null;
      const primaryIdentityId = isGuestLead ? opportunityGuest?.crm_identity_id || opportunityGuest?.id || null : null;
      const primaryContactId = isOwnerAcquisition ? ownerContact?.id || form.owner_contact_id || null : null;
      const selectedProperty = (form.properties || [])[0] || propertyValues.find((property) => property.pms_id === form.property_details);
      const metadata = {
        ...(editing ? getMetadata(editing) : {}),
        guest_lead: {
          first_name: form.guest_first_name || "",
          last_name: form.guest_last_name || "",
          phone: form.guest_phone || "",
          email: form.guest_email || "",
          display_name: opportunityGuest?.display_name || guestLabel(opportunityGuest),
          crm_identity_id: primaryIdentityId,
          guest_id: primaryGuestId,
          source: guestMode,
          check_in: form.check_in || null,
          check_out: form.check_out || null,
          guests_count: form.guests_count === "" ? null : Number(form.guests_count),
          properties: selectedProperty
            ? [
                {
                  rental_id: selectedProperty.rental_id,
                  pms_id: selectedProperty.pms_id,
                  address: selectedProperty.address || "",
                },
              ]
            : [],
          property_details: selectedProperty ? propertyLabel(selectedProperty) : form.property_details || "",
        },
      };
      if (isOwnerAcquisition) {
        metadata.owner_acquisition = {
          first_name: form.owner_first_name || "",
          last_name: form.owner_last_name || "",
          phone: ownerContact?.formatted_phone || form.owner_phone || "",
          email: form.owner_email || "",
          address: form.owner_address.trim(),
          contact_id: primaryContactId,
          crm_identity_id: ownerContact?.crm_identity_id || null,
          display_name: [form.owner_first_name, form.owner_last_name].filter(Boolean).join(" ").trim(),
        };
      }
      const pipelineId = selectedStage.pipeline_id || board.pipeline?.id || selectedPipeline.id;

      const payload = {
        ...form,
        pipeline_id: pipelineId,
        stage_id: stageId,
        title: isGuestLead ? guestLeadTitle(form) : isOwnerAcquisition ? ownerLeadTitle(form) : form.title || "Opportunity",
        primary_crm_identity_id: primaryIdentityId,
        primary_contact_id: primaryContactId,
        primary_guest_id: primaryGuestId,
        value_amount: form.value_amount === "" ? null : Number(form.value_amount),
        expected_close_date: form.expected_close_date || null,
        next_follow_up_at: form.next_follow_up_at || null,
        metadata_json: metadata,
      };

      if (editing?.id) {
        await CRMPipelineService.updateOpportunity(editing.id, payload);
        UIkit.notification("Opportunity updated", "success");
      } else {
        await CRMPipelineService.createOpportunity(payload);
        UIkit.notification("Opportunity added", "success");
      }

      closeForm();
      loadBoard(pipelineId);
    } catch (err) {
      UIkit.notification(opportunitySaveError(err), "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleDrop = async (stageId) => {
    if (!draggedId || !selectedPipeline) return;
    setDraggedId(null);

    const previous = board;
    const stages = board.stages.map((stage) => ({
      ...stage,
      opportunities: [...(stage.opportunities || [])],
    }));
    let moved = null;

    stages.forEach((stage) => {
      const idx = stage.opportunities.findIndex((item) => `${item.id}` === `${draggedId}`);
      if (idx !== -1) {
        [moved] = stage.opportunities.splice(idx, 1);
      }
    });

    if (!moved || `${moved.stage_id}` === `${stageId}`) return;

    moved = { ...moved, stage_id: stageId };
    const target = stages.find((stage) => `${stage.id}` === `${stageId}`);
    if (target) target.opportunities.unshift(moved);
    setBoard({ ...board, stages });

    try {
      await CRMPipelineService.moveOpportunity(draggedId, stageId);
    } catch (err) {
      setBoard(previous);
      UIkit.notification(err?.response?.data?.error || "Unable to move opportunity", "danger");
    }
  };

  const openDelete = (opportunity) => {
    setDeleting(opportunity);
    UIkit.modal("#modal-opportunity-delete").show();
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await CRMPipelineService.deleteOpportunity(deleting.id);
      UIkit.notification("Opportunity deleted", "success");
      UIkit.modal("#modal-opportunity-delete").hide();
      setDeleting(null);
      loadBoard(selectedPipelineId);
    } catch (err) {
      UIkit.notification(err?.response?.data?.error || "Unable to delete opportunity", "danger");
    }
  };

  return (
    <div className="crm-pipelines-page">
      <div className="crm-pipelines-header">
        <div>
          <h1>CRM Pipelines</h1>
        </div>
        <div className="crm-pipelines-actions">
          <button
            className="uk-button hms-btn hms-red-btn"
            type="button"
            onClick={() => openForm(null, board.stages?.[0])}
            disabled={!selectedPipeline}
          >
            + Opportunity
          </button>
        </div>
      </div>

      <div className="crm-pipeline-tabs">
        <ul data-uk-tab>
          {pipelines.map((pipeline) => (
            <li key={pipeline.id} className={`${pipeline.id}` === `${selectedPipelineId}` ? "uk-active" : ""}>
              <a href="#" onClick={(e) => { e.preventDefault(); handlePipelineChange(pipeline); }}>
                {pipeline.name}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {loading && !board.stages.length ? (
        <div className="uk-padding uk-text-center">Loading pipeline...</div>
      ) : (
        <div className="crm-pipeline-board">
          {(board.stages || []).map((stage) => (
            <div
              key={stage.id}
              className="crm-pipeline-column"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(stage.id)}
            >
              <div className="crm-pipeline-column-header">
                <span className="crm-pipeline-stage-color" style={{ backgroundColor: stage.color || "#e5e7eb" }} />
                <div>
                  <strong>{stage.name}</strong>
                  <span>{(stage.opportunities || []).length}</span>
                </div>
                <button
                  className="uk-button hms-btn hms-red-btn uk-button-small crm-icon-btn crm-pipeline-stage-add"
                  type="button"
                  onClick={() => openForm(null, stage)}
                  uk-tooltip="Add Opportunity"
                >
                  <FeatherIcon name="plus" size={15} />
                </button>
              </div>

              <div className="crm-pipeline-cards">
                {(stage.opportunities || []).map((opportunity) => {
                  const amount = formatMoney(opportunity.value_amount, opportunity.currency);
                  const metadata = getMetadata(opportunity);
                  const guestLead = metadata.guest_lead || {};
                  const ownerLead = metadata.owner_acquisition || {};
                  const guestName = [guestLead.first_name, guestLead.last_name].filter(Boolean).join(" ").trim() || guestLead.display_name;
                  const ownerName = [ownerLead.first_name, ownerLead.last_name].filter(Boolean).join(" ").trim() || ownerLead.display_name;
                  const guestPath = guestName ? pipelineContactPath("guest", opportunity, guestLead) : null;
                  const ownerPath = ownerName ? pipelineContactPath("owner", opportunity, ownerLead) : null;
                  const propertyText = pipelinePropertyLabel(guestLead, propertyValues);
                  const stayDates = guestLead.check_in && guestLead.check_out
                    ? `${formatDisplayDate(guestLead.check_in)} - ${formatDisplayDate(guestLead.check_out)}`
                    : "";
                  const titleName = guestName || ownerName || opportunity.title;
                  const titlePath = guestName ? guestPath : ownerName ? ownerPath : null;
                  const contactName = (name, path, className = "crm-pipeline-contact-link") => (
                    path ? (
                      <button
                        type="button"
                        className={className}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => history.push(path)}
                      >
                        {name}
                      </button>
                    ) : <span>{name}</span>
                  );
                  return (
                    <div
                      key={opportunity.id}
                      className="crm-pipeline-card"
                      draggable
                      onDragStart={() => setDraggedId(opportunity.id)}
                      onDragEnd={() => setDraggedId(null)}
                    >
                      <div className="crm-pipeline-card-title">
                        {contactName(titleName, titlePath, "crm-pipeline-card-title-link")}
                      </div>
                      {propertyText ? <div className="crm-pipeline-card-property">{propertyText}</div> : null}
                      {stayDates ? <div className="crm-pipeline-card-dates">{stayDates}</div> : null}
                      {opportunity.description ? (
                        <div className="crm-pipeline-card-description">{opportunity.description}</div>
                      ) : null}
                      <div className="crm-pipeline-card-meta">
                        {guestLead.phone ? <span>{guestLead.phone}</span> : null}
                        {ownerLead.phone ? <span>{ownerLead.phone}</span> : null}
                        {amount ? <span>{amount}</span> : null}
                        {opportunity.source ? <span>{opportunity.source}</span> : null}
                        {opportunity.expected_close_date ? (
                          <span>Close {normalizeDate(opportunity.expected_close_date)}</span>
                        ) : null}
                      </div>
                      <div className="crm-pipeline-card-actions">
                        <button type="button" onClick={() => openForm(opportunity)} title="Edit opportunity">
                          <FeatherIcon name="edit-2" size={14} />
                        </button>
                        <button type="button" onClick={() => openDelete(opportunity)} title="Delete opportunity">
                          <FeatherIcon name="trash-2" size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {!(stage.opportunities || []).length ? (
                  <button className="crm-pipeline-empty" type="button" onClick={() => openForm(null, stage)}>
                    Add the first opportunity
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        id="crm-opportunity-offcanvas"
        className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
        uk-offcanvas="flip: true; overlay: true"
      >
        <div className="uk-offcanvas-bar">
          <div className="hms-modals-main">
            <button
              className="uk-offcanvas-close uk-close-large"
              type="button"
              uk-close="true"
              onClick={closeForm}
            ></button>
            <div className="uk-modal-header">
              <h2 className="uk-modal-title crm-opportunity-modal-title">
                {editing?.id ? "Edit Opportunity" : "Add Opportunity"}
              </h2>
            </div>
            <div className="uk-modal-body crm-opportunity-offcanvas-body">
              <form onSubmit={handleSave} className="crm-modal-form crm-opportunity-form">
            <div className="uk-margin-small">
              <label className="uk-form-label">Stage*</label>
              <FormCustomSelect
                value={editing?.stage_id || board.stages?.[0]?.id || ""}
                onChange={(e) => setEditing({ ...(editing || {}), stage_id: e.target.value })}
                options={stageOptions}
              />
            </div>
            {isGuestLead && (
              <>
                <div className="crm-guest-mode">
                  <label>
                    <input
                      className="uk-radio"
                      type="radio"
                      checked={guestMode === "new"}
                      onChange={() => {
                        setGuestMode("new");
                        setSelectedGuest(null);
                        setGuestSearchTerm("");
                        setGuestSearchResults([]);
                      }}
                    />
                    New Guest
                  </label>
                  <label>
                    <input
                      className="uk-radio"
                      type="radio"
                      checked={guestMode === "existing"}
                      onChange={() => setGuestMode("existing")}
                    />
                    Existing Guest
                  </label>
                </div>
                {guestMode === "existing" && (
                  <div className="uk-margin-small crm-guest-search">
                    <label className="uk-form-label">Guest*</label>
                    <input
                      className="uk-input"
                      value={guestSearchTerm}
                      onChange={(e) => {
                        setGuestSearchTerm(e.target.value);
                        setSelectedGuest(null);
                        setGuestSearchComplete(false);
                      }}
                      placeholder="Search guests"
                    />
                    {guestSearchLoading || guestSearchResults.length || (guestSearchComplete && guestSearchTerm.trim().length >= 2) ? (
                      <div className="crm-guest-search-results">
                        {guestSearchLoading ? (
                          <div className="crm-guest-search-status">Searching...</div>
                        ) : guestSearchResults.length ? (
                          guestSearchResults.map((guest) => (
                            <button
                              key={`${guest.crm_identity_id || guest.id}-${guest.guest_id || "identity"}`}
                              type="button"
                              onClick={() => selectGuest(guest)}
                            >
                              <strong>{guestLabel(guest)}</strong>
                              <span>{guest.email || guestPhone(guest)}</span>
                            </button>
                          ))
                        ) : (
                          <div className="crm-guest-search-status">No guests found</div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
                <div className="uk-grid-small" data-uk-grid>
                  <div className="uk-width-1-2@s">
                    <label className="uk-form-label">First Name*</label>
                    <input
                      className="uk-input"
                      value={form.guest_first_name}
                      onChange={(e) => setForm({ ...form, guest_first_name: e.target.value })}
                    />
                  </div>
                  <div className="uk-width-1-2@s">
                    <label className="uk-form-label">Last Name*</label>
                    <input
                      className="uk-input"
                      value={form.guest_last_name}
                      onChange={(e) => setForm({ ...form, guest_last_name: e.target.value })}
                    />
                  </div>
                </div>
                <div className="uk-grid-small uk-margin-small" data-uk-grid>
                  <div className="uk-width-1-2@s">
                    <label className="uk-form-label">Mobile Phone*</label>
                    <input
                      className="uk-input"
                      value={form.guest_phone}
                      onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                    />
                  </div>
                  <div className="uk-width-1-2@s">
                    <label className="uk-form-label">Email</label>
                    <input
                      className="uk-input"
                      type="email"
                      value={form.guest_email}
                      onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="uk-grid-small uk-margin-small" data-uk-grid>
                  <div className="uk-width-1-2@s">
                    <label className="uk-form-label">Target Check-In</label>
                    <input
                      className="uk-input"
                      type="date"
                      value={form.check_in}
                      onChange={(e) => setForm({ ...form, check_in: e.target.value })}
                    />
                  </div>
                  <div className="uk-width-1-2@s">
                    <label className="uk-form-label">Target Check-Out</label>
                    <input
                      className="uk-input"
                      type="date"
                      value={form.check_out}
                      onChange={(e) => setForm({ ...form, check_out: e.target.value })}
                    />
                  </div>
                </div>
                <div className="uk-grid-small uk-margin-small" data-uk-grid>
                  <div className="uk-width-1-3@s">
                    <label className="uk-form-label">No. of Guests</label>
                    <FormCustomSelect
                      value={form.guests_count}
                      onChange={(e) => setForm({ ...form, guests_count: e.target.value })}
                      options={guestCountOptions}
                    />
                  </div>
                  <div className="uk-width-2-3@s">
                    <label className="uk-form-label">Property</label>
                    <SearchableDropdown
                      value={selectedPropertyValue}
                      onChange={handlePropertyChange}
                      inline
                      placeholder="Select Property"
                      options={propertyOptions}
                    />
                  </div>
                </div>
              </>
            )}
            {isOwnerAcquisition && (
              <>
                <div className="uk-grid-small" data-uk-grid>
                  <div className="uk-width-1-2@s">
                    <label className="uk-form-label">First Name*</label>
                    <input
                      className="uk-input"
                      value={form.owner_first_name}
                      onChange={(e) => setForm({ ...form, owner_first_name: e.target.value })}
                    />
                  </div>
                  <div className="uk-width-1-2@s">
                    <label className="uk-form-label">Last Name*</label>
                    <input
                      className="uk-input"
                      value={form.owner_last_name}
                      onChange={(e) => setForm({ ...form, owner_last_name: e.target.value })}
                    />
                  </div>
                </div>
                <div className="uk-grid-small uk-margin-small" data-uk-grid>
                  <div className="uk-width-1-2@s">
                    <label className="uk-form-label">Mobile Phone*</label>
                    <input
                      className="uk-input"
                      value={form.owner_phone}
                      onChange={(e) => setForm({ ...form, owner_phone: e.target.value })}
                    />
                  </div>
                  <div className="uk-width-1-2@s">
                    <label className="uk-form-label">Email</label>
                    <input
                      className="uk-input"
                      type="email"
                      value={form.owner_email}
                      onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="uk-margin-small">
                  <label className="uk-form-label" htmlFor="opportunity-owner-address">Address</label>
                  <input
                    id="opportunity-owner-address"
                    className="uk-input"
                    autoComplete="street-address"
                    value={form.owner_address}
                    onChange={(e) => setForm({ ...form, owner_address: e.target.value })}
                  />
                </div>
              </>
            )}
            <div className="uk-margin-small">
              <label className="uk-form-label">Request Details</label>
              <textarea
                className="uk-textarea"
                rows="3"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            {!isOwnerAcquisition && (
              <div className="uk-grid-small" data-uk-grid>
                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Rate</label>
                  <input
                    className="uk-input"
                    type="number"
                    min="0"
                    value={form.value_amount}
                    onChange={(e) => setForm({ ...form, value_amount: e.target.value })}
                  />
                </div>
                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Booking Source</label>
                  <input
                    className="uk-input"
                    value={form.source}
                    onChange={(e) => setForm({ ...form, source: e.target.value })}
                  />
                </div>
              </div>
            )}
            <div className="uk-grid-small uk-margin-small" data-uk-grid>
              <div className="uk-width-1-2@s">
                <label className="uk-form-label">Expected Close</label>
                <input
                  className="uk-input"
                  type="date"
                  value={form.expected_close_date}
                  onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })}
                />
              </div>
              <div className="uk-width-1-2@s">
                <label className="uk-form-label">Next Follow Up</label>
                <input
                  className="uk-input"
                  type="date"
                  value={form.next_follow_up_at}
                  onChange={(e) => setForm({ ...form, next_follow_up_at: e.target.value })}
                />
              </div>
            </div>
            <CustomFieldFormInputs
              definitions={customFieldDefinitions}
              values={customFieldValues}
              loading={customFieldsLoading}
              onChange={handleCustomFieldChange}
            />
            <div className="uk-margin-top crm-opportunity-actions">
              <button className="uk-button hms-btn hms-red-btn" type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
              <button className="uk-button hms-btn uk-offcanvas-close" type="button" disabled={saving} onClick={closeForm}>
                Cancel
              </button>
            </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Dialog id="modal-opportunity-delete">
        <div className="uk-modal-dialog uk-modal-body">
          <button
            className="uk-modal-close-default"
            type="button"
            uk-close="true"
          ></button>
          <h2 className="uk-modal-title">Delete Opportunity</h2>
          <p>
            Are you sure you want to delete <b>{deleting?.title || "this opportunity"}</b>?
          </p>
          <p className="uk-text-right">
            <button
              className="uk-button hms-btn uk-modal-close uk-modal-delete-cancel"
              type="button"
            >
              Cancel
            </button>
            <button
              className="uk-button hms-btn hms-gray-btn"
              type="button"
              onClick={handleDelete}
            >
              Delete
            </button>
          </p>
        </div>
      </Dialog>
    </div>
  );
};

export default CRMPipelines;
